import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import serviceConfig from '../serviceConfig';

const GOLD = '#C9A84C';

// ── FAQ Accordion ─────────────────────────────────────────────────────────────
function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderRadius: 14,
      border: `1px solid ${open ? GOLD + '35' : 'rgba(255,255,255,0.08)'}`,
      background: open ? `${GOLD}08` : 'rgba(255,255,255,0.03)',
      overflow: 'hidden',
      transition: 'border-color 220ms ease, background 220ms ease',
      marginBottom: '0.75rem',
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: '1rem',
          padding: 'clamp(1rem,2vw,1.25rem) clamp(1.25rem,2.5vw,1.5rem)',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontWeight: 700, color: '#fff', fontSize: 'clamp(0.9rem,1.2vw,1rem)', lineHeight: 1.4 }}>
          {question}
        </span>
        <span style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: open ? GOLD : 'rgba(255,255,255,0.08)',
          color: open ? '#000' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 220ms, color 220ms, transform 220ms',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          fontSize: '1.1rem', fontWeight: 700,
        }}>+</span>
      </button>
      {open && (
        <div style={{
          padding: '0 clamp(1.25rem,2.5vw,1.5rem) clamp(1rem,2vw,1.25rem)',
          color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.75,
        }}>
          {answer}
        </div>
      )}
    </div>
  );
}

// ── Section Heading ───────────────────────────────────────────────────────────
function SectionHeading({ label, title, span, subtitle }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 'clamp(2.5rem,5vw,4rem)' }}>
      {label && (
        <span style={{
          display: 'inline-block', fontSize: '0.7rem', fontWeight: 800,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
          border: `1px solid ${GOLD}40`, background: `${GOLD}12`,
          borderRadius: 9999, padding: '0.35rem 1rem', marginBottom: '1rem',
        }}>{label}</span>
      )}
      <h2 style={{
        fontSize: 'clamp(1.6rem,3.5vw,2.6rem)', fontWeight: 800,
        color: '#fff', margin: '0 0 1rem', lineHeight: 1.2,
      }}>
        {title}{span && <> <span style={{ color: GOLD }}>{span}</span></>}
      </h2>
      {subtitle && (
        <p style={{
          color: 'rgba(255,255,255,0.5)', fontSize: 'clamp(0.9rem,1.2vw,1.05rem)',
          maxWidth: 560, margin: '0 auto', lineHeight: 1.7,
        }}>{subtitle}</p>
      )}
    </div>
  );
}

// ── Feature icon map ──────────────────────────────────────────────────────────
const ICONS = ['🎯','⚡','🔒','📈','🛡️','🔄','🚀','💡','📊','🤝'];

// ── Category icon/emoji map ───────────────────────────────────────────────────
function categoryEmoji(name = '') {
  if (name.toLowerCase().includes('ecommerce') || name.toLowerCase().includes('commerce')) return '🛒';
  if (name.toLowerCase().includes('maintenance')) return '🛠️';
  if (name.toLowerCase().includes('copy')) return '✍️';
  if (name.toLowerCase().includes('ai')) return '🤖';
  if (name.toLowerCase().includes('marketing')) return '📈';
  if (name.toLowerCase().includes('brand')) return '🎨';
  if (name.toLowerCase().includes('software')) return '💻';
  return '🌐';
}

// ── Stats row helper ──────────────────────────────────────────────────────────
function categoryStats(categoryName = '') {
  const cat = categoryName.toLowerCase();
  if (cat.includes('ecommerce')) return [
    { value: '200+', label: 'Stores Launched' },
    { value: '35%', label: 'Avg. Sales Lift' },
    { value: '48h', label: 'Turnaround on Quotes' },
  ];
  if (cat.includes('mobile')) return [
    { value: '80+', label: 'Apps Shipped' },
    { value: 'iOS & Android', label: 'Platforms Supported' },
    { value: '4.8★', label: 'Avg. App Rating' },
  ];
  if (cat.includes('saas')) return [
    { value: '40+', label: 'SaaS Platforms Built' },
    { value: '99.9%', label: 'Uptime Delivered' },
    { value: '3×', label: 'Avg. Revenue Growth' },
  ];
  if (cat.includes('mvp')) return [
    { value: '60+', label: 'MVPs Launched' },
    { value: '8wks', label: 'Avg. Time to Market' },
    { value: '$2M+', label: 'Funding Raised by Clients' },
  ];
  if (cat.includes('integration') || cat.includes('api')) return [
    { value: '150+', label: 'APIs Connected' },
    { value: '100%', label: 'Uptime on Integrations' },
    { value: '48h', label: 'Initial Integration Time' },
  ];
  if (cat.includes('software') || cat.includes('maintenance')) return [
    { value: '99.9%', label: 'Uptime Guaranteed' },
    { value: '24/7', label: 'Monitoring & Support' },
    { value: '2h', label: 'Emergency Response' },
  ];
  if (cat.includes('digital marketing') || cat.includes('seo') || cat.includes('smm') || cat.includes('ppc') || cat.includes('orm') || cat.includes('reputation')) return [
    { value: '120+', label: 'Campaigns Managed' },
    { value: '3.5×', label: 'Avg. ROI Delivered' },
    { value: '6mo', label: 'Avg. to Measurable Results' },
  ];
  if (cat.includes('ai solutions') || cat.includes('chatbot') || cat.includes('ai poc') || (cat.includes('ai') && !cat.includes('saas') && !cat.includes('mvp'))) return [
    { value: '50+', label: 'AI Projects Delivered' },
    { value: '80%', label: 'Avg. Task Automation Rate' },
    { value: '3×', label: 'Avg. Efficiency Gain' },
  ];
  if (cat.includes('copy')) return [
    { value: '500+', label: 'Brands Written For' },
    { value: '40%', label: 'Avg. Conversion Lift' },
    { value: '48h', label: 'First Draft Delivery' },
  ];
  return [
    { value: '150+', label: 'Websites Built' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '6yrs', label: 'Industry Experience' },
  ];
}

// ── Main Component ────────────────────────────────────────────────────────────
const ServicePage = () => {
  const { servicePath } = useParams();
  const service = serviceConfig[servicePath] || {};

  const sectionStyle = {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    padding: 'clamp(4rem,8vw,6rem) clamp(1.25rem,5vw,4rem)',
  };

  const cardStyle = {
    borderRadius: 18,
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.04)',
    backdropFilter: 'blur(10px)',
    padding: 'clamp(1.5rem,2.5vw,2rem)',
    transition: 'border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease',
  };

  const stats = categoryStats(service.categoryName);

  return (
    <div style={{ background: '#080808', minHeight: '100vh', color: '#fff' }}>
      <Header />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section style={{
        position: 'relative',
        padding: 'clamp(5.5rem,10vw,8rem) clamp(1.25rem,5vw,4rem) clamp(3.5rem,7vw,5rem)',
        overflow: 'hidden',
      }}>
        {/* Glow */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: 800, height: 400, borderRadius: '50%',
          background: `radial-gradient(ellipse at center, ${GOLD}18 0%, transparent 65%)`,
          pointerEvents: 'none',
        }} />
        {/* Grid pattern */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none' }}>
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="spGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke={GOLD} strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#spGrid)" />
          </svg>
        </div>

        <div style={{ maxWidth: 860, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.75rem' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
            >Home</Link>
            <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.78rem' }}>/</span>
            <Link to="/services" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.color = '#fff'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
            >Services</Link>
            <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: '0.78rem' }}>/</span>
            <span style={{ color: GOLD, fontSize: '0.78rem', fontWeight: 600 }}>{service.name || servicePath}</span>
          </div>

          {/* Category badge */}
          {service.categoryName && (
            <span style={{
              display: 'inline-block', fontSize: '0.68rem', fontWeight: 800,
              letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
              border: `1px solid ${GOLD}40`, background: `${GOLD}12`,
              borderRadius: 9999, padding: '0.35rem 1rem', marginBottom: '1.25rem',
            }}>{service.categoryName}</span>
          )}

          {/* Title — clean, white */}
          <h1 style={{
            fontSize: 'clamp(2.2rem,5.5vw,3.8rem)', fontWeight: 900,
            color: '#fff', lineHeight: 1.1, margin: '0 0 0.75rem',
          }}>
            {service.title}
          </h1>

          {/* Title span — gold tagline, smaller */}
          {service.titleSpan && (
            <p style={{
              fontSize: 'clamp(1rem,1.8vw,1.25rem)', fontWeight: 600,
              color: GOLD, lineHeight: 1.35, margin: '0 0 1.25rem',
              opacity: 0.9,
            }}>
              {service.titleSpan}
            </p>
          )}

          {/* Description */}
          <p style={{
            fontSize: 'clamp(0.95rem,1.4vw,1.08rem)', color: 'rgba(255,255,255,0.55)',
            lineHeight: 1.8, maxWidth: 680, margin: '0 0 2.25rem',
          }}>
            {service.description}
          </p>

          {/* CTA buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.875rem', marginBottom: '3rem' }}>
            <Link
              to="/contact-us"
              className="sp-btn-primary"
              style={{
                padding: '0.9rem 2.25rem', background: GOLD,
                color: '#000', fontWeight: 800, fontSize: '0.95rem',
                borderRadius: 12, textDecoration: 'none',
                transition: 'opacity 200ms, transform 200ms',
              }}
            >
              Get Started Today →
            </Link>
            <Link
              to="/services"
              className="sp-btn-ghost"
              style={{
                padding: '0.9rem 2.25rem',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '0.95rem',
                borderRadius: 12, textDecoration: 'none',
                transition: 'border-color 200ms, color 200ms',
              }}
            >
              View All Services
            </Link>
          </div>

          {/* Stats bar */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            borderRadius: 16,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.03)',
            overflow: 'hidden',
          }}>
            {stats.map((s, i) => (
              <div key={i} style={{
                textAlign: 'center',
                padding: 'clamp(1rem,2vw,1.25rem) clamp(0.75rem,2vw,1.5rem)',
                borderRight: i < stats.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
              }}>
                <div style={{ fontSize: 'clamp(1.1rem,3vw,2rem)', fontWeight: 900, color: GOLD, marginBottom: '0.25rem' }}>{s.value}</div>
                <div style={{ fontSize: 'clamp(0.6rem,1.2vw,0.72rem)', color: 'rgba(255,255,255,0.4)', letterSpacing: '0.05em', textTransform: 'uppercase', lineHeight: 1.3 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Introduction ──────────────────────────────────────────────────── */}
      {(service.introDescription || service.intro?.description) && (
        <section style={sectionStyle}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'clamp(2rem,5vw,4rem)',
              alignItems: 'center',
            }}>
              <div>
                <span style={{
                  display: 'inline-block', fontSize: '0.7rem', fontWeight: 800,
                  letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
                  border: `1px solid ${GOLD}40`, background: `${GOLD}12`,
                  borderRadius: 9999, padding: '0.35rem 1rem', marginBottom: '1.25rem',
                }}>Overview</span>
                <h2 style={{
                  fontSize: 'clamp(1.6rem,3.5vw,2.4rem)', fontWeight: 800,
                  color: '#fff', lineHeight: 1.2, margin: '0 0 1.25rem',
                }}>
                  {service.introTitle || service.intro?.title}
                  {(service.introTitleSpan || service.intro?.titleSpan) && (
                    <> <span style={{ color: GOLD }}>{service.introTitleSpan || service.intro?.titleSpan}</span></>
                  )}
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8, marginBottom: '1rem', fontSize: '0.98rem' }}>
                  {service.introDescription || service.intro?.description}
                </p>
                {(service.introParagraph2 || service.intro?.paragraph2) && (
                  <p style={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.8, fontSize: '0.93rem' }}>
                    {service.introParagraph2 || service.intro?.paragraph2}
                  </p>
                )}
              </div>

              {/* Feature highlights card */}
              <div style={{
                ...cardStyle,
                display: 'flex', flexDirection: 'column', gap: '0',
                padding: 0, overflow: 'hidden',
              }}>
                {/* Card header */}
                <div style={{
                  padding: '1.5rem 1.75rem',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', alignItems: 'center', gap: '1rem',
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: `${GOLD}18`, border: `1px solid ${GOLD}28`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.4rem',
                  }}>
                    {categoryEmoji(service.categoryName)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 800, color: '#fff', fontSize: '0.97rem' }}>Why This Matters</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>for your business</div>
                  </div>
                </div>
                {/* Top features list */}
                <div style={{ padding: '1.25rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {(Array.isArray(service.features) ? service.features : []).slice(0, 4).map((f, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                      padding: '0.875rem 0',
                      borderBottom: i < Math.min((service.features?.length || 0), 4) - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: `${GOLD}14`, border: `1px solid ${GOLD}22`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.8rem',
                      }}>
                        <span style={{ color: GOLD, fontWeight: 900, fontSize: '0.9rem' }}>✓</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.88rem', marginBottom: '0.15rem' }}>{f.title}</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', lineHeight: 1.5 }}>{f.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Card footer CTA */}
                <div style={{
                  padding: '1rem 1.75rem',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                  background: `${GOLD}08`,
                }}>
                  <Link to="/contact-us" style={{
                    fontSize: '0.82rem', fontWeight: 700, color: GOLD, textDecoration: 'none',
                    display: 'flex', alignItems: 'center', gap: '0.4rem',
                  }}>
                    Get a free consultation <span>→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── Features Grid ─────────────────────────────────────────────────── */}
      {service.features && service.features.length > 0 && (
        <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionHeading
              label="Key Benefits"
              title={service.featuresTitle || 'What We Deliver'}
              span={service.featuresTitleSpan}
              subtitle={service.featuresDescription || service.features?.description}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'clamp(1rem,2vw,1.25rem)',
            }}>
              {(Array.isArray(service.features) ? service.features : service.features?.list || []).map((f, i) => (
                <div
                  key={f.title}
                  className="sp-feature-card"
                  style={{
                    ...cardStyle,
                    display: 'flex', flexDirection: 'column', gap: '1rem',
                    background: 'rgba(255,255,255,0.03)',
                  }}
                >
                  <div style={{
                    width: 50, height: 50, borderRadius: 14,
                    background: `${GOLD}12`, border: `1px solid ${GOLD}25`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.4rem',
                  }}>
                    {ICONS[i % ICONS.length]}
                  </div>
                  <div>
                    <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 0.5rem' }}>{f.title}</h3>
                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.86rem', lineHeight: 1.7, margin: 0 }}>{f.description}</p>
                  </div>
                  <div style={{ marginTop: 'auto', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: `${GOLD}80`, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Included
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Process Steps ─────────────────────────────────────────────────── */}
      {(service.processes?.length > 0 || service.process?.steps?.length > 0) && (
        <section style={sectionStyle}>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <SectionHeading
              label="Our Process"
              title={service.processTitle || service.process?.title || 'How We Work'}
              span={service.processTitleSpan || service.process?.titleSpan}
              subtitle={service.processDescription}
            />
            <div style={{ position: 'relative' }}>
              {/* Vertical connector line */}
              <div style={{
                position: 'absolute', left: 23, top: 48, bottom: 48,
                width: 1, background: `linear-gradient(to bottom, ${GOLD}60, ${GOLD}08)`,
              }} />
              {(service.processes || service.process?.steps || []).map((step, i, arr) => (
                <div key={i} style={{
                  display: 'flex', gap: '1.5rem', marginBottom: i < arr.length - 1 ? '1rem' : 0,
                  position: 'relative',
                }}>
                  {/* Step number circle */}
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
                    background: GOLD, color: '#000',
                    fontWeight: 900, fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 0 20px ${GOLD}40`, zIndex: 1,
                  }}>
                    {i + 1}
                  </div>
                  {/* Step card */}
                  <div style={{
                    flex: 1,
                    borderRadius: 16,
                    border: '1px solid rgba(255,255,255,0.07)',
                    background: 'rgba(255,255,255,0.03)',
                    padding: '1.1rem 1.4rem',
                    marginBottom: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', marginBottom: '0.4rem' }}>
                      <span style={{ fontSize: '0.65rem', fontWeight: 800, color: GOLD, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Step {i + 1}
                      </span>
                      <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '0.97rem', margin: 0 }}>{step.title}</h3>
                    </div>
                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.86rem', lineHeight: 1.7, margin: 0 }}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Case Studies ──────────────────────────────────────────────────── */}
      {service.caseStudies && service.caseStudies.length > 0 && (
        <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionHeading
              label="Real Results"
              title={service.caseStudiesTitle || 'Success Stories'}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'clamp(1rem,2vw,1.5rem)',
            }}>
              {service.caseStudies.map((cs, i) => (
                <div key={i} style={{
                  ...cardStyle,
                  display: 'flex', flexDirection: 'column',
                  background: 'rgba(255,255,255,0.03)',
                }}>
                  {/* Client label */}
                  <div style={{
                    fontSize: '0.68rem', fontWeight: 700, color: GOLD,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    background: `${GOLD}12`, border: `1px solid ${GOLD}28`,
                    borderRadius: 9999, padding: '0.25rem 0.75rem',
                    display: 'inline-block', alignSelf: 'flex-start',
                    marginBottom: '1rem',
                  }}>
                    {cs.client}
                  </div>
                  <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '1rem', margin: '0 0 1rem', lineHeight: 1.35 }}>{cs.title}</h3>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.25rem', flex: 1 }}>
                    {cs.results.map((r, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)' }}>
                        <span style={{ color: GOLD, fontWeight: 900, flexShrink: 0, marginTop: '0.05em' }}>✓</span> {r}
                      </li>
                    ))}
                  </ul>
                  {cs.cta && (
                    <Link to="/contact-us" style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      fontSize: '0.8rem', fontWeight: 700,
                      color: GOLD, textDecoration: 'none',
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      paddingTop: '1rem', marginTop: 'auto',
                    }}>
                      Get similar results <span>→</span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Services Sub-list ─────────────────────────────────────────────── */}
      {service.services && service.services.length > 0 && (
        <section style={sectionStyle}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <SectionHeading
              label="What We Do"
              title={service.servicesTitle || 'Our Services'}
              subtitle={service.servicesDescription}
            />
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'clamp(1rem,2vw,1.25rem)',
            }}>
              {service.services.map((s, i) => (
                <div key={i} className="sp-service-card" style={{
                  ...cardStyle,
                  display: 'flex', gap: '1rem', alignItems: 'flex-start',
                  background: 'rgba(255,255,255,0.03)',
                }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                    background: `${GOLD}12`, border: `1px solid ${GOLD}25`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.1rem',
                  }}>
                    {ICONS[i % ICONS.length]}
                  </div>
                  <div>
                    <h3 style={{ fontWeight: 800, color: '#fff', fontSize: '0.93rem', margin: '0 0 0.4rem' }}>{s.title}</h3>
                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.82rem', lineHeight: 1.65, margin: 0 }}>{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Technologies ──────────────────────────────────────────────────── */}
      {service.technologies && (
        <section style={{ ...sectionStyle, background: 'rgba(255,255,255,0.015)' }}>
          <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
            <SectionHeading
              label="Our Stack"
              title={service.technologiesTitle || 'Technologies & Platforms We Use'}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center' }}>
              {[
                ...(service.technologies.platforms || []),
                ...(service.technologies.integrations || []),
                ...(service.technologies.performanceTools || []),
                ...(service.technologies.frontEnd || []),
                ...(service.technologies.backEnd || []),
                ...(service.technologies.cmsEcommerce || []),
                ...(service.technologies.seoPerformance || []),
              ].map(tech => (
                <span key={tech} style={{
                  fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)',
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 9999, padding: '0.45rem 1.1rem',
                  transition: 'border-color 200ms, color 200ms, background 200ms',
                }}>
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      {service.faqs && service.faqs.length > 0 && (
        <section style={sectionStyle}>
          <div style={{ maxWidth: 780, margin: '0 auto' }}>
            <SectionHeading label="FAQ" title="Frequently Asked" span="Questions" />
            {service.faqs.map((faq, i) => (
              <FAQItem key={i} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>
      )}

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section style={{
        position: 'relative',
        borderTop: `1px solid ${GOLD}22`,
        padding: 'clamp(4rem,8vw,6rem) clamp(1.25rem,5vw,4rem)',
        overflow: 'hidden',
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 300, borderRadius: '50%',
          background: `radial-gradient(ellipse at center, ${GOLD}10 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <span style={{
            display: 'inline-block', fontSize: '0.68rem', fontWeight: 800,
            letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
            border: `1px solid ${GOLD}40`, background: `${GOLD}12`,
            borderRadius: 9999, padding: '0.35rem 1rem', marginBottom: '1.25rem',
          }}>Ready to Start?</span>

          <h2 style={{
            fontSize: 'clamp(1.6rem,4vw,2.8rem)', fontWeight: 900,
            color: '#fff', margin: '0 0 1rem', lineHeight: 1.15,
          }}>
            Let's Build Something{' '}
            <span style={{ color: GOLD }}>That Actually Works</span>
          </h2>

          <p style={{
            color: 'rgba(255,255,255,0.5)', fontSize: '1rem', lineHeight: 1.75,
            margin: '0 auto 2.5rem', maxWidth: 520,
          }}>
            Tell us about your project and we'll put together a custom proposal — no pressure, no cookie-cutter packages.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', marginBottom: '2.5rem' }}>
            <Link
              to="/contact-us"
              style={{
                padding: '1rem 2.5rem', background: GOLD,
                color: '#000', fontWeight: 800, fontSize: '1rem',
                borderRadius: 12, textDecoration: 'none',
                transition: 'opacity 200ms, transform 200ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Get a Free Quote →
            </Link>
            <a
              href="tel:+18259063078"
              style={{
                padding: '1rem 2.5rem',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.7)', fontWeight: 600, fontSize: '1rem',
                borderRadius: 12, textDecoration: 'none',
                transition: 'border-color 200ms, color 200ms',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = `${GOLD}60`; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            >
              Call +1 825 906 3078
            </a>
          </div>

          {/* Trust row */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center',
          }}>
            {['Response within 2 hours', 'No obligation quote', 'Dedicated project manager'].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.38)' }}>
                <span style={{ color: GOLD, fontWeight: 900, fontSize: '0.85rem' }}>✓</span> {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        .sp-feature-card:hover {
          border-color: rgba(201,168,76,0.3) !important;
          transform: translateY(-3px) !important;
          box-shadow: 0 16px 40px rgba(0,0,0,0.4);
        }
        .sp-service-card:hover {
          border-color: rgba(201,168,76,0.28) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 12px 32px rgba(0,0,0,0.35);
        }
        .sp-btn-primary:hover {
          opacity: 0.88 !important;
          transform: translateY(-2px) !important;
        }
        .sp-btn-ghost:hover {
          border-color: rgba(201,168,76,0.5) !important;
          color: #fff !important;
        }
        @media (max-width: 480px) {
          .sp-btn-primary, .sp-btn-ghost {
            width: 100%;
            text-align: center;
            display: block;
            box-sizing: border-box;
          }
        }
      `}</style>
    </div>
  );
};

export default ServicePage;
